import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Mock user data - will be replaced with real backend
const mockUsers = [
  {
    id: 1,
    email: 'demo@dmv-lernportal.de',
    name: 'Demo Benutzer',
    password: 'demo123',
    currentLanguage: 'de',
    currentLevel: 'A1',
    profile: {
      avatar: null,
      joinDate: '2024-01-15',
      streak: 7,
      totalReadingTime: 45,
      completedStories: [1],
      learnedWords: 25,
      achievements: ['first_story', 'word_collector', 'streak_7'],
      preferences: {
        ttsEnabled: true,
        ttsSpeed: 1.0,
        ttsVoice: 'neural',
        autoPlay: false,
        theme: 'light'
      }
    }
  }
];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for stored user session
    const storedUser = localStorage.getItem('dmv_user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        localStorage.removeItem('dmv_user');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (email, password) => {
    setIsLoading(true);
    try {
      // Mock login - replace with real API call
      const foundUser = mockUsers.find(u => u.email === email && u.password === password);
      if (foundUser) {
        const { password: _, ...userWithoutPassword } = foundUser;
        setUser(userWithoutPassword);
        setIsAuthenticated(true);
        localStorage.setItem('dmv_user', JSON.stringify(userWithoutPassword));
        return { success: true };
      } else {
        return { success: false, error: 'Ungültige Anmeldedaten' };
      }
    } catch (error) {
      return { success: false, error: 'Anmeldung fehlgeschlagen' };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData) => {
    setIsLoading(true);
    try {
      // Mock registration - replace with real API call
      const existingUser = mockUsers.find(u => u.email === userData.email);
      if (existingUser) {
        return { success: false, error: 'E-Mail bereits registriert' };
      }

      const newUser = {
        id: Date.now(),
        email: userData.email,
        name: userData.name,
        currentLanguage: userData.targetLanguage || 'de',
        currentLevel: 'A1',
        profile: {
          avatar: null,
          joinDate: new Date().toISOString().split('T')[0],
          streak: 0,
          totalReadingTime: 0,
          completedStories: [],
          learnedWords: 0,
          achievements: [],
          preferences: {
            ttsEnabled: true,
            ttsSpeed: 1.0,
            ttsVoice: 'neural',
            autoPlay: false,
            theme: 'light'
          }
        }
      };

      mockUsers.push({ ...newUser, password: userData.password });
      const { password: _, ...userWithoutPassword } = newUser;
      setUser(userWithoutPassword);
      setIsAuthenticated(true);
      localStorage.setItem('dmv_user', JSON.stringify(userWithoutPassword));
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Registrierung fehlgeschlagen' };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('dmv_user');
  };

  const updateProfile = async (updates) => {
    if (!user) return { success: false, error: 'Nicht angemeldet' };

    try {
      const updatedUser = {
        ...user,
        ...updates,
        profile: {
          ...user.profile,
          ...(updates.profile || {})
        }
      };

      setUser(updatedUser);
      localStorage.setItem('dmv_user', JSON.stringify(updatedUser));
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Profil-Update fehlgeschlagen' };
    }
  };

  const updateProgress = (progressData) => {
    if (!user) return;

    const updatedUser = {
      ...user,
      profile: {
        ...user.profile,
        ...progressData
      }
    };

    setUser(updatedUser);
    localStorage.setItem('dmv_user', JSON.stringify(updatedUser));
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    updateProfile,
    updateProgress
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};